import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Typography, theme } from 'antd';
import { StatusTag } from '../../../components/StatusTag';
import { useT } from '../../../locales';
import '../roles.css';

export interface RoleEditorHeaderProps {
  title: string;
  description?: string;
  isActive: boolean;
  selectedPermissionCount: number;
  selectedModuleCount: number;
  isDirty: boolean;
  readOnly?: boolean;
  saving?: boolean;
  saveLabel?: string;
  onBack: () => void;
  onDiscard: () => void;
  formId: string;
}

export function RoleEditorHeader({
  title,
  description,
  isActive,
  selectedPermissionCount,
  selectedModuleCount,
  isDirty,
  readOnly = false,
  saving = false,
  saveLabel,
  onBack,
  onDiscard,
  formId,
}: RoleEditorHeaderProps) {
  const t = useT();
  const { token } = theme.useToken();

  return (
    <header
      className="role-editor-header"
      style={{ borderBottom: `1px solid ${token.colorBorderSecondary}` }}
    >
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        style={{ alignSelf: 'flex-start', paddingInline: 0 }}
      >
        {t('pages.roles.backToRoles')}
      </Button>

      <div className="role-editor-header__identity">
        <div className="role-editor-header__title">
          <Typography.Title level={2} style={{ margin: 0, fontSize: 24 }}>
            {title}
          </Typography.Title>
          <Typography.Text
            type="secondary"
            ellipsis={{ tooltip: true }}
            style={{ display: 'block', marginTop: 6, maxWidth: '70ch' }}
          >
            {description?.trim() || t('pages.roles.noDescription')}
          </Typography.Text>
        </div>

        <div className="role-editor-header__summary" aria-label={t('pages.roles.summaryLabel')}>
          <StatusTag tone={isActive ? 'success' : 'neutral'}>
            {isActive ? t('pages.roles.active') : t('pages.roles.inactive')}
          </StatusTag>
          <StatusTag tone="info">
            {t('pages.roles.permissionCount', { count: selectedPermissionCount })}
          </StatusTag>
          <StatusTag tone="neutral">
            {t('pages.roles.moduleCount', { count: selectedModuleCount })}
          </StatusTag>
        </div>
      </div>

      <div className="role-editor-header__actions">
        <Button onClick={onDiscard} disabled={!isDirty || saving || readOnly}>
          {t('pages.roles.discard')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          form={formId}
          loading={saving}
          disabled={!isDirty || readOnly}
        >
          {saveLabel ?? t('pages.roles.saveChanges')}
        </Button>
      </div>
    </header>
  );
}
